import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { RefreshReactQuery } from "../../constants/AppConstants";
import { ERROR_LABEL, ERROR_MESSAGE_LABEL } from "../../constants/Dictionary";
import CmSheetNotesService from "../../services/api/cmSheet/CmSheetNotes.service";
import { UpdateCollaboratorsCmSheetNotesRequest } from "../../types/api/collaborators.type";
import { useNotification } from "../notification.hook";

export const CM_SHEET_NOTES_QUERY_KEY = "get-cm-sheet-notes";

export const useCmSheetNotes = (cmSheetId: number) => {
  const { notification } = useNotification();
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: [CM_SHEET_NOTES_QUERY_KEY, cmSheetId],
    queryFn: () => CmSheetNotesService.list(cmSheetId),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useUpdateCmSheetNotes = (cmSheetId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync: updateCmSheetNotes, isPending: isUpdating } =
    useMutation({
      mutationFn: (body: UpdateCollaboratorsCmSheetNotesRequest) =>
        CmSheetNotesService.update(cmSheetId, body),
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [CM_SHEET_NOTES_QUERY_KEY],
        });
      },
      onError: (error) => {
        console.error(error);
        notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
      },
    });

  return { updateCmSheetNotes, isUpdating };
};
